import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore, ServiceStore } from "@intrasync/stores";
import type {
  TcpEmployeeHours,
  TcpHoursResponse,
} from "types/api-types/system/tcp-hours/tcp-hours.types";
import type { tcpEmployee } from "types/api-types/system/tcp-employees/tcp-employees.types";
import type { evereeWorkerData } from "types/api-types/payroll/everee-worker-data/everee-worker-data.types";
import type {
  TimesheetImportShift,
  TimesheetImportResult,
} from "types/api-types/payroll/timesheet-import/timesheet-import.types";

export const STEP_LABELS = [
  "Fetch Hours",
  "Map Employees",
  "Import",
  "Verify",
] as const;

const toEpochSeconds = (iso: string): number =>
  Math.floor(new Date(iso).getTime() / 1000);

class TcpImportStore {
  rootStore: RootStore;

  // Wizard state
  currentStep = 0;
  isLoading = false;
  error: string | null = null;

  // Step 1 - Fetch Hours
  startDate = "";
  stopDate = "";
  tcpHoursData: TcpEmployeeHours[] = [];
  selectedEmployeeIds: Set<string> = new Set();

  // Step 2 - Mapping
  tcpEmployees: tcpEmployee[] = [];
  employeeMapping: Map<string, string> = new Map(); // tcpEmployeeId → evereeWorkerId
  workerData: evereeWorkerData[] = [];

  // Step 3 - Import
  importResult: TimesheetImportResult | null = null;
  isImporting = false;

  // DataGrid headers for verification step
  currentHeaders = [
    { key: "shiftId", title: "Shift ID", width: 120 },
    { key: "workerName", title: "Worker", width: 180 },
    { key: "inAt", title: "Clock In", width: 160 },
    { key: "outAt", title: "Clock Out", width: 160 },
    { key: "hours", title: "Hours", width: 80 },
    { key: "status", title: "Status", width: 100 },
  ];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ServiceStore accessors
  get evereeTimesheetsStore(): ServiceStore {
    return this.rootStore.evereeTimesheetsStore as ServiceStore;
  }

  get evereeWorkerDataStore(): ServiceStore {
    return this.rootStore.evereeWorkerDataStore as ServiceStore;
  }

  // Computed values
  get unmappedCount(): number {
    let count = 0;
    this.selectedEmployeeIds.forEach((id) => {
      if (!this.employeeMapping.has(id)) count++;
    });
    return count;
  }

  get mappedCount(): number {
    return this.selectedEmployeeIds.size - this.unmappedCount;
  }

  get canProceedFromStep1(): boolean {
    return this.tcpHoursData.length > 0 && this.selectedEmployeeIds.size > 0;
  }

  get canProceedFromStep2(): boolean {
    return this.unmappedCount === 0 && this.selectedEmployeeIds.size > 0;
  }

  get importShifts(): TimesheetImportShift[] {
    return this.buildImportShifts();
  }

  get workerMap(): Map<string, string> {
    const map = new Map<string, string>();
    this.workerData.forEach((w) => {
      map.set(w.workerId, `${w.firstName} ${w.lastName}`);
    });
    return map;
  }

  get verificationTimesheets(): Record<string, unknown>[] {
    const timesheets = (this.evereeTimesheetsStore?.entities || []) as unknown as Array<{
      shiftId: string;
      workerId: string;
      inAt?: string;
      outAt?: string;
      hours?: number;
      status?: string;
    }>;
    return timesheets.map((t) => ({
      id: t.shiftId,
      shiftId: t.shiftId,
      workerId: t.workerId,
      workerName: this.workerMap.get(t.workerId) || t.workerId,
      inAt: t.inAt ? new Date(t.inAt).toLocaleString() : "",
      outAt: t.outAt ? new Date(t.outAt).toLocaleString() : "",
      hours: t.hours?.toFixed(2) || "",
      status: t.status || "",
    }));
  }

  // Step navigation
  setStep = (step: number) => {
    if (step >= 0 && step < STEP_LABELS.length) {
      this.currentStep = step;
    }
  };

  nextStep = () => {
    this.setStep(this.currentStep + 1);
  };

  prevStep = () => {
    this.setStep(this.currentStep - 1);
  };

  // Step 1 actions
  setStartDate = (date: string) => {
    this.startDate = date;
  };

  setStopDate = (date: string) => {
    this.stopDate = date;
  };

  toggleEmployeeSelection = (employeeId: string) => {
    if (this.selectedEmployeeIds.has(employeeId)) {
      this.selectedEmployeeIds.delete(employeeId);
    } else {
      this.selectedEmployeeIds.add(employeeId);
    }
  };

  selectAllEmployees = () => {
    this.tcpHoursData.forEach((eh) => {
      this.selectedEmployeeIds.add(eh.employee.EmployeeId);
    });
  };

  deselectAllEmployees = () => {
    this.selectedEmployeeIds.clear();
  };

  fetchTcpHours = async () => {
    if (!this.startDate || !this.stopDate) {
      this.error = "Start date and stop date are required";
      return;
    }

    this.isLoading = true;
    this.error = null;

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const app = (this.rootStore as any).app;
      if (!app) throw new Error("API not available");

      const companyId = this.rootStore.routerStore?.queryParams?.companyId as string;

      const response: TcpHoursResponse = await app.service("tcp-hours").find({
        query: {
          startDate: this.startDate,
          stopDate: this.stopDate,
          companyId,
        },
      });

      runInAction(() => {
        this.tcpHoursData = response.data || [];
        // Select all employees by default
        this.selectedEmployeeIds.clear();
        this.tcpHoursData.forEach((eh) => {
          this.selectedEmployeeIds.add(eh.employee.EmployeeId);
        });
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to fetch TCP hours";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Step 2 actions
  loadWorkerData = async (companyId?: string) => {
    try {
      const query: Record<string, unknown> = { page: 0, size: 100 };
      if (companyId) query.companyId = companyId;

      await this.evereeWorkerDataStore.find({ query });

      runInAction(() => {
        const raw = (this.evereeWorkerDataStore?.entities || []) as unknown as (Record<string, unknown>)[];
        // API returns 'id' but our type uses 'workerId' - normalize the field
        this.workerData = raw.map((e) => ({
          ...e,
          workerId: (e.workerId || e.id) as string,
        })) as unknown as evereeWorkerData[];
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to load worker data";
      });
    }
  };

  fetchTcpEmployees = async () => {
    this.isLoading = true;
    this.error = null;

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const app = (this.rootStore as any).app;
      if (!app) throw new Error("API not available");

      const result = await app.service("tcp-employees").find({
        query: { pageSize: 100, isActive: true },
      });

      runInAction(() => {
        this.tcpEmployees = Array.isArray(result) ? result : result.data || [];
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to fetch TCP employees";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  autoMapEmployees = () => {
    this.employeeMapping.clear();

    const workers = this.workerData;

    this.tcpHoursData.forEach((eh) => {
      const tcpId = eh.employee.EmployeeId;
      const tcpFirstName = eh.employee.FirstName?.toLowerCase().trim() || "";
      const tcpLastName = eh.employee.LastName?.toLowerCase().trim() || "";

      // Also try TCP employees list for additional matching data
      const tcpEmp = this.tcpEmployees.find((te) => te.id === tcpId);
      const tcpEmail = tcpEmp?.email?.toLowerCase().trim() || "";
      const tcpPayrollId = tcpEmp?.payrollId?.trim() || "";

      // Try matching strategies in priority order
      for (const worker of workers) {
        const wFirstName = worker.firstName?.toLowerCase().trim() || "";
        const wLastName = worker.lastName?.toLowerCase().trim() || "";
        const wEmail = worker.email?.toLowerCase().trim() || "";
        const wExternalId = worker.externalWorkerId?.trim() || "";

        // Match by payrollId → externalWorkerId
        if (tcpPayrollId && wExternalId && tcpPayrollId === wExternalId) {
          this.employeeMapping.set(tcpId, worker.workerId);
          return;
        }

        // Match by email
        if (tcpEmail && wEmail && tcpEmail === wEmail) {
          this.employeeMapping.set(tcpId, worker.workerId);
          return;
        }

        // Match by first + last name
        if (
          tcpFirstName &&
          tcpLastName &&
          tcpFirstName === wFirstName &&
          tcpLastName === wLastName
        ) {
          this.employeeMapping.set(tcpId, worker.workerId);
          return;
        }
      }
    });
  };

  setEmployeeMapping = (tcpEmployeeId: string, evereeWorkerId: string) => {
    if (evereeWorkerId) {
      this.employeeMapping.set(tcpEmployeeId, evereeWorkerId);
    } else {
      this.employeeMapping.delete(tcpEmployeeId);
    }
  };

  // Step 3 actions
  buildImportShifts = (): TimesheetImportShift[] => {
    const shifts: TimesheetImportShift[] = [];

    this.tcpHoursData.forEach((eh) => {
      const tcpId = eh.employee.EmployeeId;

      // Skip if not selected
      if (!this.selectedEmployeeIds.has(tcpId)) return;

      const workerId = this.employeeMapping.get(tcpId);
      if (!workerId) return;

      // Filter out leave and still-clocked-in segments
      const validSegments = eh.segments.filter(
        (seg) => !seg.isLeave && !seg.clockedInStatus && seg.timeIn && seg.timeOut
      );

      if (validSegments.length === 0) return;

      // Group segments by date (using timeIn date)
      const byDate = new Map<string, typeof validSegments>();
      validSegments.forEach((seg) => {
        const date = seg.timeIn.split("T")[0];
        if (!byDate.has(date)) byDate.set(date, []);
        byDate.get(date)!.push(seg);
      });

      // Create one shift per date with breaks for gaps
      byDate.forEach((daySegments) => {
        // Sort by timeIn
        daySegments.sort(
          (a, b) => new Date(a.timeIn).getTime() - new Date(b.timeIn).getTime()
        );

        const shiftStart = toEpochSeconds(daySegments[0].timeIn);
        const shiftEnd = toEpochSeconds(daySegments[daySegments.length - 1].timeOut);

        // Build breaks from gaps between consecutive segments
        const breaks: { breakStartEpochSeconds: number; breakEndEpochSeconds: number; isPaid: boolean }[] = [];
        for (let i = 0; i < daySegments.length - 1; i++) {
          const currentEnd = toEpochSeconds(daySegments[i].timeOut);
          const nextStart = toEpochSeconds(daySegments[i + 1].timeIn);
          if (nextStart > currentEnd) {
            breaks.push({
              breakStartEpochSeconds: currentEnd,
              breakEndEpochSeconds: nextStart,
              isPaid: false,
            });
          }
        }

        const shift: TimesheetImportShift = {
          workerId,
          shiftStartEpochSeconds: shiftStart,
          shiftEndEpochSeconds: shiftEnd,
        };

        if (breaks.length > 0) {
          shift.createBreaks = breaks;
        }

        shifts.push(shift);
      });
    });

    return shifts;
  };

  submitImport = async () => {
    const shifts = this.importShifts;
    if (shifts.length === 0) {
      this.error = "No shifts to import";
      return;
    }

    this.isImporting = true;
    this.error = null;
    this.importResult = null;

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const app = (this.rootStore as any).app;
      if (!app) throw new Error("API not available");

      const companyId = this.rootStore.routerStore?.queryParams?.companyId as string;

      const result: TimesheetImportResult = await app.service("timesheet-import").create({
        shifts,
        companyId,
      });

      runInAction(() => {
        this.importResult = result;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to submit import";
      });
    } finally {
      runInAction(() => {
        this.isImporting = false;
      });
    }
  };

  retryFailed = async () => {
    if (!this.importResult) return;

    const allShifts = this.importShifts;
    const failedIndices = this.importResult.results
      .filter((r) => !r.success)
      .map((r) => r.index);

    const failedShifts = failedIndices
      .map((i) => allShifts[i])
      .filter(Boolean);

    if (failedShifts.length === 0) return;

    this.isImporting = true;
    this.error = null;

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const app = (this.rootStore as any).app;
      if (!app) throw new Error("API not available");

      const companyId = this.rootStore.routerStore?.queryParams?.companyId as string;

      const result: TimesheetImportResult = await app.service("timesheet-import").create({
        shifts: failedShifts,
        companyId,
      });

      runInAction(() => {
        this.importResult = result;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to retry import";
      });
    } finally {
      runInAction(() => {
        this.isImporting = false;
      });
    }
  };

  // Step 4 actions
  loadVerification = async () => {
    if (!this.startDate || !this.stopDate) return;

    this.isLoading = true;
    this.error = null;

    try {
      const companyId = this.rootStore.routerStore?.queryParams?.companyId as string;
      const query: Record<string, unknown> = {
        $limit: 1000,
        startDate: `${this.startDate}T00:00:00Z`,
        endDate: `${this.stopDate}T23:59:59Z`,
        status: "PENDING",
      };
      if (companyId) query.companyId = companyId;

      await this.evereeTimesheetsStore.find({ query });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to load verification data";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Reset
  reset = () => {
    this.currentStep = 0;
    this.isLoading = false;
    this.error = null;
    this.startDate = "";
    this.stopDate = "";
    this.tcpHoursData = [];
    this.selectedEmployeeIds.clear();
    this.tcpEmployees = [];
    this.employeeMapping.clear();
    this.workerData = [];
    this.importResult = null;
    this.isImporting = false;
  };
}

export default TcpImportStore;
